<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4 shadow-sm-custom'
      ]"
      style="box-shadow: var(--shadow-sm)"
    >
      <div class="flex items-center justify-between">
        <div>
          <h1
            :class="[theme.isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-2xl font-bold']"
          >
            Platform Reports
          </h1>
          <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm']">
            Configure and manage automated HTTP → Template → Slack reports
          </p>
        </div>
        <button
          class="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-200 flex items-center space-x-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-400 shadow-md-custom shadow-hover-md"
          style="box-shadow: var(--shadow-md)"
          @click="openCreateModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clip-rule="evenodd"
            />
          </svg>
          <span>Create Report</span>
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6 space-y-6">
      <!-- Stats Summary -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4 shadow-sm-custom shadow-hover-md transition-all duration-200'
          ]"
          style="box-shadow: var(--shadow-sm)"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="text-2xl font-bold text-gray-900 dark:text-white">{{ stats.total }}</div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Total Reports</div>
            </div>
            <div class="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg
                class="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4 shadow-sm-custom shadow-hover-md transition-all duration-200'
          ]"
          style="box-shadow: var(--shadow-sm)"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {{ stats.active }}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Active</div>
            </div>
            <div class="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <svg
                class="w-6 h-6 text-emerald-600 dark:text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
        </div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4 shadow-sm-custom shadow-hover-md transition-all duration-200'
          ]"
          style="box-shadow: var(--shadow-sm)"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {{ stats.withPDF }}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">With PDF/File</div>
            </div>
            <div class="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <svg
                class="w-6 h-6 text-amber-600 dark:text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4 shadow-sm-custom shadow-hover-md transition-all duration-200'
          ]"
          style="box-shadow: var(--shadow-sm)"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="text-2xl font-bold text-violet-600 dark:text-violet-400">
                {{ stats.scheduled }}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Scheduled (Cron)</div>
            </div>
            <div class="p-3 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
              <svg
                class="w-6 h-6 text-violet-600 dark:text-violet-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Bulk Actions Bar -->
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="selectedCount > 0"
          :class="[
            theme.isDark.value
              ? 'bg-emerald-900/20 border-emerald-700/50'
              : 'bg-emerald-50 border-emerald-200',
            'border rounded-lg p-4 shadow-lg-custom flex items-center justify-between'
          ]"
          style="box-shadow: var(--shadow-lg)"
        >
          <div class="flex items-center space-x-3">
            <div
              class="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-white font-semibold text-sm shadow-md-custom"
              style="box-shadow: var(--shadow-md)"
            >
              {{ selectedCount }}
            </div>
            <div>
              <p class="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                {{ selectedCount }} {{ selectedCount === 1 ? 'report' : 'reports' }} selected
              </p>
              <p class="text-xs text-emerald-700 dark:text-emerald-300">
                Click to select or deselect individual reports
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <button
              class="px-4 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 dark:hover:text-emerald-100 transition-colors"
              @click="clearSelection"
            >
              Clear Selection
            </button>
            <button
              class="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-medium transition-all duration-200 shadow-md-custom shadow-hover-md flex items-center space-x-2"
              style="box-shadow: var(--shadow-md)"
              :disabled="isBulkDeleting"
              @click="confirmBulkDelete"
            >
              <svg
                v-if="isBulkDeleting"
                class="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                v-else
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span>{{ isBulkDeleting ? 'Deleting...' : `Delete ${selectedCount}` }}</span>
            </button>
          </div>
        </div>
      </transition>

      <!-- Search and Filters -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-4 shadow-sm-custom'
        ]"
        style="box-shadow: var(--shadow-sm)"
      >
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <!-- Search -->
          <div class="relative flex-1 max-w-md">
            <input
              v-model="searchQuery"
              type="text"
              class="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-100 focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:border-emerald-500 dark:focus:border-emerald-400"
              placeholder="Search by name, URL, or template..."
            />
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                class="h-5 w-5 text-gray-400 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          <!-- Filter Buttons -->
          <div class="flex items-center gap-2">
            <button
              v-for="filter in filterOptions"
              :key="filter.value"
              :class="[
                activeFilter === filter.value
                  ? 'bg-emerald-500 text-white border-emerald-500 shadow-md-custom'
                  : theme.isDark.value
                    ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 shadow-xs shadow-hover-sm'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 shadow-xs shadow-hover-sm',
                'px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200'
              ]"
              :style="
                activeFilter === filter.value
                  ? 'box-shadow: var(--shadow-md);'
                  : 'box-shadow: var(--shadow-xs);'
              "
              @click="activeFilter = filter.value"
            >
              {{ filter.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <LoadingState
        v-if="isLoading"
        message="Loading reports..."
      />

      <!-- Error State -->
      <div
        v-else-if="error"
        :class="[
          theme.isDark.value ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200',
          'border rounded-lg p-6 text-center shadow-md-custom'
        ]"
        style="box-shadow: var(--shadow-md)"
      >
        <svg
          class="mx-auto h-12 w-12 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-red-700 dark:text-red-300">{{ error }}</h3>
        <button
          class="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
          @click="fetchReports"
        >
          Try Again
        </button>
      </div>

      <!-- Reports Table -->
      <div
        v-else
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg overflow-hidden shadow-md-custom'
        ]"
        style="box-shadow: var(--shadow-md)"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-800/80">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.field"
                  class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  :class="{
                    'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700': column.sortable,
                    'w-12': column.field === 'select'
                  }"
                  @click="column.sortable && handleSort(column.field)"
                >
                  <div
                    v-if="column.field === 'select'"
                    class="flex items-center justify-center"
                  >
                    <input
                      type="checkbox"
                      :checked="isAllSelected"
                      :indeterminate="isSomeSelected"
                      class="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-400 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600 cursor-pointer transition-all duration-200"
                      @click.stop
                      @change="toggleSelectAll"
                    />
                  </div>
                  <div
                    v-else
                    class="flex items-center space-x-1"
                  >
                    <span>{{ column.label }}</span>
                    <span
                      v-if="column.sortable"
                      class="flex flex-col ml-1"
                    >
                      <svg
                        class="w-3 h-3"
                        :class="
                          sortField === column.field && sortOrder === 'asc'
                            ? 'text-emerald-500'
                            : 'text-gray-400'
                        "
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 3l-7 7h14l-7-7z" />
                      </svg>
                      <svg
                        class="w-3 h-3 -mt-1"
                        :class="
                          sortField === column.field && sortOrder === 'desc'
                            ? 'text-emerald-500'
                            : 'text-gray-400'
                        "
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 17l7-7H3l7 7z" />
                      </svg>
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody
              class="bg-white dark:bg-gray-800/90 divide-y divide-gray-200 dark:divide-gray-700/30"
            >
              <template
                v-for="report in filteredReports"
                :key="report.id"
              >
                <!-- Main Row -->
                <tr
                  :class="[
                    'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200',
                    selectedReports.has(report.id) ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : ''
                  ]"
                  style="
                    transition:
                      background-color 0.2s ease-in-out,
                      box-shadow 0.2s ease-in-out;
                  "
                >
                  <td
                    class="px-6 py-4"
                    @click.stop="toggleSelect(report.id)"
                  >
                    <div class="flex items-center justify-center">
                      <input
                        type="checkbox"
                        :checked="selectedReports.has(report.id)"
                        class="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-400 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600 cursor-pointer transition-all duration-200 shadow-xs"
                        style="box-shadow: var(--shadow-xs)"
                        @click.stop
                        @change="toggleSelect(report.id)"
                      />
                    </div>
                  </td>
                  <td
                    class="px-6 py-4 cursor-pointer"
                    @click="toggleExpand(report.id)"
                  >
                    <div class="flex items-center">
                      <div
                        :class="[
                          report.status === 'active'
                            ? 'bg-emerald-500'
                            : report.status === 'draft'
                              ? 'bg-amber-500'
                              : 'bg-gray-400',
                          'w-2 h-2 rounded-full mr-3'
                        ]"
                      ></div>
                      <div>
                        <div class="text-sm font-medium text-gray-900 dark:text-white">
                          {{ report.name || report.id }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
                          <span
                            class="inline-flex items-center px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]"
                            :title="report.id"
                          >
                            {{ report.id }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    class="px-6 py-4 cursor-pointer"
                    @click="toggleExpand(report.id)"
                  >
                    <div class="flex items-center">
                      <span
                        :class="[
                          getScheduleTypeColor(report.schedule?.type),
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                        ]"
                      >
                        <span class="mr-1">{{ getScheduleIcon(report.schedule?.type) }}</span>
                        {{ report.schedule?.type || 'manual' }}
                      </span>
                    </div>
                    <div
                      v-if="report.schedule?.expression"
                      class="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono"
                    >
                      {{ report.schedule.expression }}
                    </div>
                  </td>
                  <td
                    class="px-6 py-4 cursor-pointer"
                    @click="toggleExpand(report.id)"
                  >
                    <span
                      :class="[
                        getMethodColor(report.source.config.method),
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                      ]"
                    >
                      {{ report.source.config.method }}
                    </span>
                  </td>
                  <td
                    class="px-6 py-4 cursor-pointer"
                    @click="toggleExpand(report.id)"
                  >
                    <div class="flex flex-col">
                      <div class="flex items-center space-x-2">
                        <span
                          :class="[
                            getSinkActionColor(report.sink.config.action),
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                          ]"
                        >
                          <span class="mr-1">{{ getSinkIcon(report.sink.config.action) }}</span>
                          {{ formatSinkAction(report.sink.config.action) }}
                        </span>
                        <span
                          v-if="report.transform.config.fileConfig?.enabled"
                          class="text-amber-500"
                          title="Includes PDF/File"
                        >
                          📎
                        </span>
                      </div>
                      <span
                        v-if="
                          report.sink.config.userId ||
                          report.sink.config.channelId ||
                          (report.sink.config.userIds && report.sink.config.userIds.length > 0)
                        "
                        class="text-xs text-gray-500 dark:text-gray-400 font-mono mt-1 truncate max-w-[150px]"
                        :title="
                          report.sink.config.userId ||
                          report.sink.config.channelId ||
                          (report.sink.config.userIds ? report.sink.config.userIds.join(', ') : '')
                        "
                      >
                        {{
                          report.sink.config.userId ||
                          report.sink.config.channelId ||
                          (report.sink.config.userIds
                            ? `${report.sink.config.userIds.length} users`
                            : '')
                        }}
                      </span>
                    </div>
                  </td>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 cursor-pointer"
                    @click="toggleExpand(report.id)"
                  >
                    {{ formatDate(report.updatedAt) }}
                  </td>
                  <td
                    class="px-6 py-4 cursor-pointer"
                    @click="toggleExpand(report.id)"
                  >
                    <button
                      :class="[
                        report.status === 'active'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                          : report.status === 'draft'
                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
                        'px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize'
                      ]"
                      @click.stop="toggleActive(report)"
                    >
                      {{ report.status }}
                    </button>
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex items-center space-x-1">
                      <!-- Trigger Test Button -->
                      <div class="relative group">
                        <button
                          class="p-1.5 text-emerald-500 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-all duration-200 shadow-xs shadow-hover-sm"
                          style="box-shadow: var(--shadow-xs)"
                          :class="{
                            'opacity-50 cursor-not-allowed': testingReportId === report.id
                          }"
                          :disabled="testingReportId === report.id"
                          @click.stop="testReport(report)"
                        >
                          <svg
                            v-if="testingReportId !== report.id"
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <svg
                            v-else
                            class="w-4 h-4 animate-spin"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                        </button>
                        <div
                          class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10"
                        >
                          Trigger
                          <div
                            class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"
                          ></div>
                        </div>
                      </div>

                      <!-- Edit Button -->
                      <div class="relative group">
                        <button
                          class="p-1.5 text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200 shadow-xs shadow-hover-sm"
                          style="box-shadow: var(--shadow-xs)"
                          @click.stop="openEditModal(report)"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <div
                          class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10"
                        >
                          Edit
                          <div
                            class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"
                          ></div>
                        </div>
                      </div>

                      <!-- Duplicate Button -->
                      <div class="relative group">
                        <button
                          class="p-1.5 text-violet-500 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-lg transition-all duration-200 shadow-xs shadow-hover-sm"
                          style="box-shadow: var(--shadow-xs)"
                          @click.stop="duplicateReport(report)"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                        <div
                          class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10"
                        >
                          Duplicate
                          <div
                            class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"
                          ></div>
                        </div>
                      </div>

                      <!-- Delete Button -->
                      <div class="relative group">
                        <button
                          class="p-1.5 text-rose-500 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-all duration-200 shadow-xs shadow-hover-sm"
                          style="box-shadow: var(--shadow-xs)"
                          @click.stop="confirmDelete(report)"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                        <div
                          class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10"
                        >
                          Delete
                          <div
                            class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>

                <!-- Expanded Row -->
                <tr
                  v-if="expandedRows.has(report.id)"
                  class="bg-gray-50 dark:bg-gray-700/30"
                >
                  <td
                    colspan="8"
                    class="px-6 py-4"
                  >
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <!-- HTTP Source Configuration -->
                      <div class="space-y-3">
                        <h4
                          class="text-sm font-semibold text-gray-900 dark:text-white flex items-center"
                        >
                          <svg
                            class="w-4 h-4 mr-2 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                            />
                          </svg>
                          HTTP Source
                        </h4>
                        <div
                          class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-xs"
                          style="box-shadow: var(--shadow-xs)"
                        >
                          <div class="space-y-2 text-sm">
                            <div class="flex items-center justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Method:</span>
                              <span
                                :class="[
                                  getMethodColor(report.source.config.method),
                                  'px-2 py-0.5 rounded text-xs font-medium'
                                ]"
                              >
                                {{ report.source.config.method }}
                              </span>
                            </div>
                            <div>
                              <span class="text-gray-500 dark:text-gray-400">URL:</span>
                              <div
                                class="mt-1 font-mono text-xs text-gray-900 dark:text-white break-all bg-gray-100 dark:bg-gray-900 p-2 rounded"
                              >
                                {{ report.source.config.url }}
                              </div>
                            </div>
                            <div
                              v-if="report.source.config.timeout"
                              class="flex items-center justify-between"
                            >
                              <span class="text-gray-500 dark:text-gray-400">Timeout:</span>
                              <span class="font-medium text-gray-900 dark:text-white"
                                >{{ report.source.config.timeout / 1000 }}s</span
                              >
                            </div>
                            <div
                              v-if="
                                report.source.config.headers &&
                                Object.keys(report.source.config.headers).length > 0
                              "
                            >
                              <span class="text-gray-500 dark:text-gray-400">Headers:</span>
                              <div class="mt-1 space-y-1">
                                <div
                                  v-for="(value, key) in report.source.config.headers"
                                  :key="key"
                                  class="flex items-center text-xs"
                                >
                                  <span class="font-mono text-violet-600 dark:text-violet-400"
                                    >{{ key }}:</span
                                  >
                                  <span
                                    class="ml-2 font-mono text-gray-600 dark:text-gray-400 truncate"
                                    >{{ maskSensitiveValue(key as string, value) }}</span
                                  >
                                </div>
                              </div>
                            </div>
                            <div v-if="report.source.config.body">
                              <span class="text-gray-500 dark:text-gray-400">Body:</span>
                              <pre
                                class="mt-1 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-800 dark:text-gray-200 overflow-x-auto"
                                >{{ JSON.stringify(report.source.config.body, null, 2) }}</pre
                              >
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Transform Configuration -->
                      <div class="space-y-3">
                        <h4
                          class="text-sm font-semibold text-gray-900 dark:text-white flex items-center"
                        >
                          <svg
                            class="w-4 h-4 mr-2 text-violet-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          Template Transform
                        </h4>
                        <div
                          class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-xs"
                          style="box-shadow: var(--shadow-xs)"
                        >
                          <div class="space-y-3">
                            <div>
                              <span class="text-xs text-gray-500 dark:text-gray-400"
                                >Handlebars Template:</span
                              >
                              <pre
                                class="mt-1 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-800 dark:text-gray-200 overflow-x-auto max-h-32 whitespace-pre-wrap"
                                >{{ report.transform.config.template }}</pre
                              >
                            </div>
                            <div
                              v-if="report.transform.config.fileConfig?.enabled"
                              class="pt-3 border-t border-gray-200 dark:border-gray-600"
                            >
                              <span
                                class="text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center"
                              >
                                📎 PDF/File Generation Enabled
                              </span>
                              <div class="mt-2 space-y-1 text-xs">
                                <div
                                  v-if="report.transform.config.fileConfig.dataPath"
                                  class="flex items-center justify-between"
                                >
                                  <span class="text-gray-500 dark:text-gray-400">Data Path:</span>
                                  <span class="font-mono text-gray-900 dark:text-white">{{
                                    report.transform.config.fileConfig.dataPath
                                  }}</span>
                                </div>
                                <div
                                  v-if="
                                    report.transform.config.fileConfig.headers &&
                                    report.transform.config.fileConfig.headers.length > 0
                                  "
                                  class="flex items-center justify-between"
                                >
                                  <span class="text-gray-500 dark:text-gray-400">Headers:</span>
                                  <span class="font-mono text-gray-900 dark:text-white">{{
                                    report.transform.config.fileConfig.headers.join(', ')
                                  }}</span>
                                </div>
                                <div
                                  v-if="report.transform.config.fileConfig.topN"
                                  class="flex items-center justify-between"
                                >
                                  <span class="text-gray-500 dark:text-gray-400">Top N:</span>
                                  <span class="font-medium text-gray-900 dark:text-white">{{
                                    report.transform.config.fileConfig.topN
                                  }}</span>
                                </div>
                                <div
                                  v-if="report.transform.config.fileConfig.sort"
                                  class="flex items-center justify-between"
                                >
                                  <span class="text-gray-500 dark:text-gray-400">Sort:</span>
                                  <span class="font-mono text-gray-900 dark:text-white">
                                    {{ report.transform.config.fileConfig.sort.columns.join(', ') }}
                                    <span class="text-gray-500"
                                      >({{
                                        report.transform.config.fileConfig.sort.order || 'desc'
                                      }})</span
                                    >
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Slack Sink Configuration -->
                      <div class="space-y-3">
                        <h4
                          class="text-sm font-semibold text-gray-900 dark:text-white flex items-center"
                        >
                          <svg
                            class="w-4 h-4 mr-2 text-emerald-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                          </svg>
                          Slack Sink
                        </h4>
                        <div
                          class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-xs"
                          style="box-shadow: var(--shadow-xs)"
                        >
                          <div class="space-y-2 text-sm">
                            <div class="flex items-center justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Action:</span>
                              <span
                                :class="[
                                  getSinkActionColor(report.sink.config.action),
                                  'px-2 py-0.5 rounded-full text-xs font-medium'
                                ]"
                              >
                                {{ formatSinkAction(report.sink.config.action) }}
                              </span>
                            </div>
                            <div
                              v-if="report.sink.config.userId"
                              class="flex items-center justify-between"
                            >
                              <span class="text-gray-500 dark:text-gray-400">User ID:</span>
                              <span class="font-mono text-xs text-gray-900 dark:text-white">{{
                                report.sink.config.userId
                              }}</span>
                            </div>
                            <div
                              v-if="
                                report.sink.config.userIds && report.sink.config.userIds.length > 0
                              "
                              class="flex items-center justify-between"
                            >
                              <span class="text-gray-500 dark:text-gray-400">User IDs:</span>
                              <span class="font-mono text-xs text-gray-900 dark:text-white"
                                >{{ report.sink.config.userIds.length }} users</span
                              >
                            </div>
                            <div
                              v-if="report.sink.config.channelId"
                              class="flex items-center justify-between"
                            >
                              <span class="text-gray-500 dark:text-gray-400">Channel ID:</span>
                              <span class="font-mono text-xs text-gray-900 dark:text-white">{{
                                report.sink.config.channelId
                              }}</span>
                            </div>
                            <div
                              v-if="report.sink.config.threadTs"
                              class="flex items-center justify-between"
                            >
                              <span class="text-gray-500 dark:text-gray-400">Thread TS:</span>
                              <span class="font-mono text-xs text-gray-900 dark:text-white">{{
                                report.sink.config.threadTs
                              }}</span>
                            </div>
                          </div>
                          <div
                            v-if="report.sink.config.file"
                            class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600"
                          >
                            <span class="text-xs font-medium text-amber-600 dark:text-amber-400"
                              >📎 File Attachment</span
                            >
                            <div class="mt-1 space-y-1 text-xs">
                              <div class="flex items-center justify-between">
                                <span class="text-gray-500 dark:text-gray-400">Filename:</span>
                                <span class="text-gray-900 dark:text-white font-mono">{{
                                  report.sink.config.file.filename
                                }}</span>
                              </div>
                              <div
                                v-if="report.sink.config.file.title"
                                class="flex items-center justify-between"
                              >
                                <span class="text-gray-500 dark:text-gray-400">Title:</span>
                                <span class="text-gray-900 dark:text-white">{{
                                  report.sink.config.file.title
                                }}</span>
                              </div>
                              <div
                                v-if="report.sink.config.file.beforeContent"
                                class="flex items-start justify-between"
                              >
                                <span class="text-gray-500 dark:text-gray-400"
                                  >Before Content:</span
                                >
                                <span
                                  class="text-gray-900 dark:text-white text-right max-w-[200px]"
                                  >{{ report.sink.config.file.beforeContent }}</span
                                >
                              </div>
                              <div
                                v-if="report.sink.config.file.afterContent"
                                class="flex items-start justify-between"
                              >
                                <span class="text-gray-500 dark:text-gray-400">After Content:</span>
                                <span
                                  class="text-gray-900 dark:text-white text-right max-w-[200px]"
                                  >{{ report.sink.config.file.afterContent }}</span
                                >
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Schedule Info -->
                        <div
                          v-if="report.schedule?.expression"
                          class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-xs"
                          style="box-shadow: var(--shadow-xs)"
                        >
                          <h5
                            class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center"
                          >
                            <svg
                              class="w-3 h-3 mr-1.5 text-blue-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Schedule
                          </h5>
                          <div class="space-y-2 text-xs">
                            <div>
                              <span class="text-gray-500 dark:text-gray-400">Human readable:</span>
                              <div class="mt-1 text-gray-700 dark:text-gray-300">
                                {{ parseCronExpression(report.schedule.expression) }}
                              </div>
                            </div>
                            <div v-if="getISTTime(report.schedule.expression)">
                              <span class="text-gray-500 dark:text-gray-400">IST Equivalent:</span>
                              <div class="mt-1 text-emerald-600 dark:text-emerald-400 font-medium">
                                {{ getISTTime(report.schedule.expression) }}
                              </div>
                            </div>
                            <div v-if="report.meta?.nextRun">
                              <span class="text-gray-500 dark:text-gray-400">Next Run:</span>
                              <div class="mt-1 text-violet-600 dark:text-violet-400 font-medium">
                                {{ formatEpochToIST(report.meta.nextRun) }}
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Metadata Info -->
                        <div
                          class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-xs"
                          style="box-shadow: var(--shadow-xs)"
                        >
                          <h5
                            class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center"
                          >
                            <svg
                              class="w-3 h-3 mr-1.5 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Metadata
                          </h5>
                          <div class="space-y-2 text-xs">
                            <div class="flex items-center justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Created By:</span>
                              <span class="font-mono text-gray-900 dark:text-white">{{
                                getEmailPrefix(report.meta?.createdBy)
                              }}</span>
                            </div>
                            <div class="flex items-center justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Updated By:</span>
                              <span class="font-mono text-gray-900 dark:text-white">{{
                                getEmailPrefix(report.meta?.updatedBy)
                              }}</span>
                            </div>
                            <div class="flex items-center justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Last Updated:</span>
                              <span class="text-gray-900 dark:text-white">{{
                                formatDate(report.updatedAt)
                              }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>

              <!-- Empty State -->
              <tr v-if="filteredReports.length === 0">
                <td
                  colspan="8"
                  class="px-6 py-12 text-center"
                >
                  <svg
                    class="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                    No reports found
                  </h3>
                  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {{
                      searchQuery
                        ? 'Try adjusting your search or filters'
                        : 'Get started by creating a new report'
                    }}
                  </p>
                  <button
                    v-if="!searchQuery"
                    class="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-200 text-sm font-medium shadow-md-custom shadow-hover-md"
                    style="box-shadow: var(--shadow-md)"
                    @click="openCreateModal"
                  >
                    Create Report
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          :class="[
            theme.isDark.value ? 'bg-gray-800/80 border-gray-700' : 'bg-gray-50 border-gray-200',
            'px-6 py-4 border-t flex items-center justify-between'
          ]"
        >
          <div class="text-sm text-gray-500 dark:text-gray-400">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to
            {{ Math.min(currentPage * pageSize, totalReports) }} of {{ totalReports }} reports
          </div>
          <div class="flex items-center space-x-2">
            <button
              :disabled="currentPage === 1"
              :class="[
                currentPage === 1
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-700',
                theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors'
              ]"
              @click="prevPage"
            >
              Previous
            </button>
            <div class="flex items-center space-x-1">
              <template
                v-for="page in totalPages"
                :key="page"
              >
                <button
                  v-if="
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  "
                  :class="[
                    page === currentPage
                      ? 'bg-emerald-500 text-white'
                      : theme.isDark.value
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-200',
                    'w-8 h-8 rounded-lg text-sm font-medium transition-colors'
                  ]"
                  @click="goToPage(page)"
                >
                  {{ page }}
                </button>
                <span
                  v-else-if="page === currentPage - 2 || page === currentPage + 2"
                  class="text-gray-400"
                >
                  ...
                </span>
              </template>
            </div>
            <button
              :disabled="currentPage === totalPages"
              :class="[
                currentPage === totalPages
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-700',
                theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors'
              ]"
              @click="nextPage"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
        <div
          class="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
          @click="showDeleteModal = false"
        ></div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800' : 'bg-white',
            'relative inline-block rounded-xl text-left overflow-hidden shadow-modal transform transition-all sm:my-8 sm:max-w-lg sm:w-full'
          ]"
          style="box-shadow: var(--shadow-modal)"
        >
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-center">
              <div
                class="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-900/30 shadow-md-custom"
                style="box-shadow: var(--shadow-md)"
              >
                <svg
                  class="h-6 w-6 text-rose-600 dark:text-rose-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  {{
                    reportsToDelete.length > 1
                      ? `Delete ${reportsToDelete.length} Reports`
                      : 'Delete Report'
                  }}
                </h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  <span v-if="reportsToDelete.length === 1">
                    Are you sure you want to delete "{{
                      reportToDelete?.name || reportToDelete?.id
                    }}"? This action cannot be undone.
                  </span>
                  <span v-else>
                    Are you sure you want to delete {{ reportsToDelete.length }} reports? This
                    action cannot be undone.
                  </span>
                </p>
                <div
                  v-if="reportsToDelete.length > 1"
                  class="mt-3 max-h-32 overflow-y-auto"
                >
                  <ul class="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <li
                      v-for="report in reportsToDelete"
                      :key="report.id"
                      class="flex items-center"
                    >
                      <span class="w-1.5 h-1.5 rounded-full bg-rose-500 mr-2"></span>
                      {{ report.name || report.id }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex justify-end space-x-3">
            <button
              :class="[
                theme.isDark.value
                  ? 'bg-gray-600 hover:bg-gray-500 text-gray-200'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors'
              ]"
              @click="showDeleteModal = false"
            >
              Cancel
            </button>
            <button
              class="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-medium transition-all duration-200 shadow-md-custom shadow-hover-md flex items-center space-x-2"
              style="box-shadow: var(--shadow-md)"
              :disabled="isDeleting || isBulkDeleting"
              @click="deleteReport"
            >
              <svg
                v-if="isDeleting || isBulkDeleting"
                class="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{{ isDeleting || isBulkDeleting ? 'Deleting...' : 'Delete' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <Toast
      v-model:show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :duration="4000"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, inject, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import type { ReportConfig, ListReportsParams, ReportStatus } from '../../types/platform-report'
import { platformReportsService } from '../../services/platform-reports'
import Toast from '../../components/base/common/Toast.vue'
import LoadingState from '../../components/base/common/LoadingState.vue'
import { toString } from 'cronstrue'
import authService from '../../services/auth/auth'

const theme = inject('theme', useTheme())
const router = useRouter()

// State
const reports = ref<ReportConfig[]>([])
const expandedRows = ref<Set<string>>(new Set())
const selectedReports = ref<Set<string>>(new Set())
const searchQuery = ref('')
const activeFilter = ref('all')
const sortField = ref<'createdAt' | 'updatedAt' | 'name'>('updatedAt')
const sortOrder = ref<'asc' | 'desc'>('desc')

// Pagination
const currentPage = ref(1)
const pageSize = ref(20)
const totalReports = ref(0)
const totalPages = ref(0)

// Loading & Error State
const isLoading = ref(false)
const isDeleting = ref(false)
const error = ref<string | null>(null)

// Modal State
const showDeleteModal = ref(false)
const reportToDelete = ref<ReportConfig | null>(null)
const reportsToDelete = ref<ReportConfig[]>([])
const testingReportId = ref<string | null>(null)
const isBulkDeleting = ref(false)

// Toast
const toast = reactive({
  show: false,
  message: '',
  type: 'success' as 'success' | 'error'
})

// Filter Options
const filterOptions = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'With PDF', value: 'withPdf' },
  { label: 'Slack', value: 'slack' }
]

// Columns
const columns = [
  { field: 'select', label: '', sortable: false },
  { field: 'name', label: 'Report Name', sortable: true },
  { field: 'schedule', label: 'Schedule', sortable: false },
  { field: 'method', label: 'Method', sortable: false },
  { field: 'sink', label: 'Destination', sortable: false },
  { field: 'updatedAt', label: 'Last Updated', sortable: true },
  { field: 'status', label: 'Status', sortable: false },
  { field: 'actions', label: 'Actions', sortable: false }
]

// Computed
const stats = computed(() => ({
  total: totalReports.value,
  active: reports.value.filter((r) => r.status === 'active').length,
  withPDF: reports.value.filter((r) => r.transform?.config?.fileConfig?.enabled).length,
  scheduled: reports.value.filter((r) => r.schedule?.type === 'cron').length
}))

// Reports are already filtered and sorted by the API
const filteredReports = computed(() => reports.value)

// Computed for selection
const isAllSelected = computed(() => {
  return (
    filteredReports.value.length > 0 && selectedReports.value.size === filteredReports.value.length
  )
})

const isSomeSelected = computed(() => {
  return selectedReports.value.size > 0 && selectedReports.value.size < filteredReports.value.length
})

const selectedCount = computed(() => selectedReports.value.size)

// Fetch reports from API
const fetchReports = async () => {
  isLoading.value = true
  error.value = null

  try {
    // Build API params
    const params: ListReportsParams = {
      page: currentPage.value,
      limit: pageSize.value,
      sortBy: sortField.value,
      sortOrder: sortOrder.value
    }

    // Add search if present
    if (searchQuery.value) {
      params.search = searchQuery.value
    }

    // Add status filter
    if (
      activeFilter.value === 'active' ||
      activeFilter.value === 'inactive' ||
      activeFilter.value === 'draft'
    ) {
      params.status = activeFilter.value as ReportStatus
    }

    // Add sink type filter
    if (activeFilter.value === 'slack') {
      params.sinkType = 'slack'
    }

    const response = await platformReportsService.listReports(params)

    // Apply client-side filter for withPdf (since mock doesn't support this filter)
    if (activeFilter.value === 'withPdf') {
      response.reports = response.reports.filter((r) => r.transform?.config?.fileConfig?.enabled)
      response.total = response.reports.length
      response.totalPages = Math.ceil(response.total / pageSize.value)
    }

    reports.value = response.reports
    totalReports.value = response.total
    totalPages.value = response.totalPages

    // Clear selections when data changes
    selectedReports.value.clear()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch reports'
    showToast(error.value, 'error')
  } finally {
    isLoading.value = false
  }
}

// Debounced search
let searchTimeout: ReturnType<typeof setTimeout> | null = null
const debouncedFetch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    fetchReports()
  }, 300)
}

// Watch for filter/sort changes
watch([activeFilter, sortField, sortOrder], () => {
  currentPage.value = 1
  fetchReports()
})

watch(searchQuery, () => {
  debouncedFetch()
})

watch(currentPage, () => {
  fetchReports()
})

// Methods
const handleSort = (field: string) => {
  const sortableFields = ['name', 'createdAt', 'updatedAt']
  if (!sortableFields.includes(field)) return

  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field as 'name' | 'createdAt' | 'updatedAt'
    sortOrder.value = 'asc'
  }
}

const toggleExpand = (id: string) => {
  if (expandedRows.value.has(id)) {
    expandedRows.value.delete(id)
  } else {
    expandedRows.value.add(id)
  }
}

const openCreateModal = () => {
  router.push('/platform-report/create')
}

const openEditModal = (report: ReportConfig) => {
  router.push(`/platform-report/edit/${report.id}`)
}

const confirmDelete = (report: ReportConfig) => {
  reportToDelete.value = report
  reportsToDelete.value = [report]
  showDeleteModal.value = true
}

const confirmBulkDelete = () => {
  if (selectedReports.value.size === 0) return

  const selected = reports.value.filter((r) => selectedReports.value.has(r.id))
  reportsToDelete.value = selected
  reportToDelete.value = null
  showDeleteModal.value = true
}

const deleteReport = async () => {
  // Handle single delete
  if (reportToDelete.value) {
    isDeleting.value = true
    try {
      await platformReportsService.deleteReport(reportToDelete.value.id)
      showDeleteModal.value = false
      reportToDelete.value = null
      reportsToDelete.value = []
      showToast('Report deleted successfully', 'success')
      await fetchReports()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete report'
      showToast(errorMsg, 'error')
    } finally {
      isDeleting.value = false
    }
    return
  }

  // Handle bulk delete with Promise.all
  if (reportsToDelete.value.length > 0) {
    isBulkDeleting.value = true
    isDeleting.value = true

    try {
      // Use Promise.all to delete all reports in parallel
      const deletePromises = reportsToDelete.value.map((report) =>
        platformReportsService.deleteReport(report.id)
      )

      const deleteCount = reportsToDelete.value.length
      await Promise.all(deletePromises)

      // Clear selections
      selectedReports.value.clear()
      showDeleteModal.value = false
      reportsToDelete.value = []
      reportToDelete.value = null

      showToast(
        `Successfully deleted ${deleteCount} ${deleteCount === 1 ? 'report' : 'reports'}`,
        'success'
      )

      // Refresh the list
      await fetchReports()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete reports'
      showToast(errorMsg, 'error')
    } finally {
      isBulkDeleting.value = false
      isDeleting.value = false
    }
  }
}

// Selection methods
const toggleSelect = (id: string) => {
  if (selectedReports.value.has(id)) {
    selectedReports.value.delete(id)
  } else {
    selectedReports.value.add(id)
  }
}

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedReports.value.clear()
  } else {
    filteredReports.value.forEach((report) => {
      selectedReports.value.add(report.id)
    })
  }
}

const clearSelection = () => {
  selectedReports.value.clear()
}

const duplicateReport = async (report: ReportConfig) => {
  try {
    // Get current user email
    const user = authService.getUser()
    const userEmail = user?.email || ''

    // Create a copy of the report without the id
    const duplicateData = {
      name: `${report.name || report.id} (Copy)`,
      schedule: report.schedule,
      status: 'draft' as ReportStatus,
      source: report.source,
      transform: report.transform,
      sink: report.sink,
      meta: {
        createdBy: userEmail
      }
    }

    await platformReportsService.createReport(duplicateData)
    showToast('Report duplicated successfully', 'success')
    // Refresh the list
    await fetchReports()
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Failed to duplicate report'
    showToast(errorMsg, 'error')
  }
}

const toggleActive = async (report: ReportConfig) => {
  try {
    // Get current user email
    const user = authService.getUser()
    const userEmail = user?.email || ''

    const newStatus: ReportStatus = report.status === 'active' ? 'inactive' : 'active'
    await platformReportsService.updateReport(report.id, {
      status: newStatus,
      meta: {
        updatedBy: userEmail
      }
    })
    // Update local state
    report.status = newStatus
    showToast(`Report ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success')
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Failed to update report status'
    showToast(errorMsg, 'error')
  }
}

const testReport = async (report: ReportConfig) => {
  testingReportId.value = report.id

  try {
    const result = await platformReportsService.executeReport(report.id)
    showToast(result.message || `Test completed for "${report.name || report.id}"`, 'success')
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Failed to execute report'
    showToast(errorMsg, 'error')
  } finally {
    testingReportId.value = null
  }
}

// Pagination methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const showToast = (message: string, type: 'success' | 'error') => {
  toast.message = message
  toast.type = type
  toast.show = true
}

const formatDate = (dateString?: string) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Extract email prefix (part before @) or return 'NA'
const getEmailPrefix = (email?: string) => {
  if (!email) return 'NA'
  const parts = email.split('@')
  return parts.length > 0 ? parts[0] : 'NA'
}

// Format epoch timestamp (milliseconds) to IST
const formatEpochToIST = (epochMs?: number | string) => {
  if (!epochMs) return '-'
  // Handle both number and string (for backward compatibility)
  const timestamp = typeof epochMs === 'string' ? parseInt(epochMs, 10) : epochMs
  if (isNaN(timestamp)) return '-'
  const date = new Date(timestamp)
  return `${date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })} IST`
}

const getMethodColor = (method: string) => {
  const colors: Record<string, string> = {
    GET: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    POST: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    PUT: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    PATCH: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
    DELETE: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400'
  }
  return colors[method] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
}

const getSinkActionColor = (action: string) => {
  const colors: Record<string, string> = {
    sendToDM: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    sendToChannel: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    sendToThread: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400'
  }
  return colors[action] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
}

const getSinkIcon = (action: string) => {
  const icons: Record<string, string> = {
    sendToDM: '👤',
    sendToChannel: '#️⃣',
    sendToThread: '🧵'
  }
  return icons[action] || '💬'
}

const formatSinkAction = (action: string) => {
  const labels: Record<string, string> = {
    sendToDM: 'DM',
    sendToChannel: 'Channel',
    sendToThread: 'Thread'
  }
  return labels[action] || action
}

const getScheduleTypeColor = (type?: string) => {
  const colors: Record<string, string> = {
    cron: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    manual: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  }
  return colors[type || 'manual'] || colors.manual
}

const getScheduleIcon = (type?: string) => {
  const icons: Record<string, string> = {
    cron: '🕐',
    manual: '👆'
  }
  return icons[type || 'manual'] || icons.manual
}

const maskSensitiveValue = (key: string, value: string) => {
  const sensitiveKeys = ['authorization', 'x-api-key', 'token', 'secret', 'password']
  if (sensitiveKeys.some((k) => key.toLowerCase().includes(k))) {
    return `${value.substring(0, 10)}...`
  }
  return value
}

const parseCronExpression = (expression: string) => {
  if (!expression || expression.trim() === '') {
    return expression
  }

  try {
    return toString(expression, {
      use24HourTimeFormat: false,
      throwExceptionOnParseError: false
    })
  } catch {
    return expression
  }
}

// Get IST equivalent time for cron expression
const getISTTime = (expression: string): string | null => {
  if (!expression || expression.trim() === '') {
    return null
  }

  try {
    const parts = expression.trim().split(/\s+/)
    if (parts.length !== 5) {
      return null
    }

    const [minute, hour] = parts

    // Only show IST conversion if we have specific hour and minute (not wildcards)
    if (hour === '*' || minute === '*') {
      return null
    }

    const utcHour = parseInt(hour, 10)
    const utcMinute = parseInt(minute, 10)

    if (isNaN(utcHour) || isNaN(utcMinute)) {
      return null
    }

    // IST is UTC + 5:30
    let istHour = utcHour + 5
    let istMinute = utcMinute + 30

    // Handle minute overflow
    if (istMinute >= 60) {
      istMinute -= 60
      istHour += 1
    }

    // Handle hour overflow
    if (istHour >= 24) {
      istHour -= 24
    }

    // Format time
    const period = istHour >= 12 ? 'PM' : 'AM'
    const displayHour = istHour === 0 ? 12 : istHour > 12 ? istHour - 12 : istHour

    // Get the rest of the cron expression for context
    const [, , dayOfMonth, month, dayOfWeek] = parts
    let schedulePart = ''

    if (dayOfWeek === '1-5' && dayOfMonth === '*' && month === '*') {
      schedulePart = ' on weekdays (Monday-Friday)'
    } else if (dayOfWeek === '*' && dayOfMonth === '*' && month === '*') {
      schedulePart = ' daily'
    } else if (dayOfWeek !== '*' && dayOfMonth === '*' && month === '*') {
      schedulePart = ` on ${getDayName(dayOfWeek)}`
    } else {
      schedulePart = ' (same schedule pattern)'
    }

    return `At ${displayHour}:${istMinute.toString().padStart(2, '0')} ${period} IST${schedulePart}`
  } catch {
    return null
  }
}

// Helper to get day name from cron day of week
const getDayName = (dayOfWeek: string): string => {
  const dayMap: Record<string, string> = {
    '0': 'Sunday',
    '1': 'Monday',
    '2': 'Tuesday',
    '3': 'Wednesday',
    '4': 'Thursday',
    '5': 'Friday',
    '6': 'Saturday',
    '7': 'Sunday'
  }

  if (dayOfWeek.includes('-')) {
    const [start, end] = dayOfWeek.split('-')
    return `${dayMap[start] || start}-${dayMap[end] || end}`
  }

  if (dayOfWeek.includes(',')) {
    return dayOfWeek
      .split(',')
      .map((d) => dayMap[d.trim()] || d.trim())
      .join(', ')
  }

  return dayMap[dayOfWeek] || dayOfWeek
}

// Lifecycle
onMounted(() => {
  fetchReports()
})
</script>
